import type { SldLink } from "../services/calculationRun";
import { StatusIcon } from "./StatusIcon";

// The study-to-SLD link of a stored run (SLD-b1, A27 (c), (d)) in the shared UX-1 contracts:
// the banner says which diagram version the run was checked against and whether the check
// was clean; a REVIEW outcome lists one review-tone card per warning, with the readable
// name of its code, its element and the server's sentence. Nothing is shown for a run
// whose revision had no diagram (the panel is not rendered at all).

export const SLD_LINK_CODE_LABELS: Record<string, string> = {
  SLD_ELEMENT_NOT_FOUND: "Element not in the diagram",
  SLD_ELEMENT_WRONG_KIND: "Element of another kind",
  SLD_VOLTAGE_MISMATCH: "Voltage differs from the diagram",
  SLD_NOT_ON_BUS: "Not on the study's bus",
  SLD_RATING_DIFFERS: "Rating differs from the diagram",
};

export function sldLinkStatusText(link: SldLink): string {
  return link.status === "VALID"
    ? `Linked to SLD version ${link.sld_version}: valid`
    : `Linked to SLD version ${link.sld_version}: review required`;
}

type SldLinkPanelProps = {
  link: SldLink | null | undefined;
};

export function SldLinkPanel({ link }: SldLinkPanelProps) {
  if (!link) {
    return null;
  }
  const valid = link.status === "VALID";
  return (
    <section aria-label="SLD link" data-summary-tone={valid ? "pass" : "warning"}>
      <header>
        <p data-summary-status={link.status}>
          <StatusIcon tone={valid ? "pass" : "review"} size={20} />
          {sldLinkStatusText(link)}
        </p>
        <p>
          Content hash <code>{link.sld_content_hash.slice(0, 12)}</code>
          {valid
            ? " · every bus of the study is in the diagram at its voltage."
            : ` · ${link.warnings.length} item(s) to review.`}
        </p>
      </header>
      {link.warnings.length > 0 ? (
        <ul data-warning-count={link.warnings.length}>
          {link.warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${warning.element}-${index}`}
              data-warning-code={warning.code}
              data-warning-tone="review"
              data-sld-element={warning.element}
            >
              <strong>
                <StatusIcon tone="review" size={16} />
                {SLD_LINK_CODE_LABELS[warning.code] ?? warning.code}
              </strong>
              {": "}
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default SldLinkPanel;
